import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/feature/MovieCard';
import MovieListItem from '@/components/feature/MovieListItem';
import EmptyState from '@/components/feature/EmptyState';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { TYPE_LABELS, getHotMovies, searchMovies } from '@/utils/movie';
import type { MovieType } from '@/mocks/movies';

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: '全部类型' },
  { value: 'movie', label: '电影' },
  { value: 'tv', label: '电视剧' },
  { value: 'variety', label: '综艺' },
];

const YEAR_OPTIONS = ['全部年份', '2025', '2024', '2023'];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('全部年份');

  useEffect(() => {
    setInput(keyword);
    setTypeFilter('all');
    setYearFilter('全部年份');
  }, [keyword]);

  useDocumentMeta({
    title: keyword ? `搜索“${keyword}” - 丝瓜视频` : '搜索影视 - 丝瓜视频',
    description: keyword
      ? `丝瓜视频搜索“${keyword}”的结果页，为你展示匹配的电影、电视剧与综艺节目。`
      : '在丝瓜视频搜索电影、电视剧与综艺节目，快速找到想看的影视内容。',
    keywords: '丝瓜视频,影视搜索,电影搜索,电视剧搜索,综艺搜索',
  });

  const baseResult = useMemo(() => searchMovies(keyword), [keyword]);

  const result = useMemo(() => {
    let list = [...baseResult];
    if (typeFilter !== 'all') list = list.filter((m) => m.type === (typeFilter as MovieType));
    if (yearFilter !== '全部年份') list = list.filter((m) => String(m.year) === yearFilter);
    return list;
  }, [baseResult, typeFilter, yearFilter]);

  const hot = getHotMovies(4);
  const isTypeFiltered = typeFilter !== 'all' || yearFilter !== '全部年份';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setSearchParams(kw ? { q: kw } : {});
  };

  const resetFilters = () => {
    setTypeFilter('all');
    setYearFilter('全部年份');
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1360px] px-4 py-6 md:px-6 md:py-10">
        <header className="border-b border-background-200 pb-6">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">搜索影视</h1>
          <p className="mt-2 text-sm text-foreground-600">输入片名、类型、导演或演员，快速找到想看的内容。</p>

          <form onSubmit={submit} className="mt-5 flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="search"
                placeholder="搜索影片、剧集、综艺"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <button
              type="submit"
              className="flex h-11 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-6 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-2-line" /> 搜索
            </button>
          </form>
        </header>

        {keyword ? (
          <section className="mt-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-foreground-600">
                关键词 <strong className="text-primary-600">“{keyword}”</strong> 共找到
                <strong className="mx-1 text-foreground-950">{result.length}</strong> 个结果
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                >
                  {YEAR_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                {isTypeFiltered && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-refresh-line" /> 清除筛选
                  </button>
                )}
              </div>
            </div>

            {result.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                {result.map((movie) => (
                  <MovieListItem key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <>
                <div className="mt-6">
                  <EmptyState
                    icon="ri-search-eye-line"
                    title="未找到匹配内容"
                    description={`没有找到与“${keyword}”相关的影视，换个关键词试试，或看看下面的热门推荐。`}
                  />
                </div>
                <div className="mt-10">
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-fire-line text-accent-600" /> 你可能想看
                  </h2>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {hot.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>
        ) : (
          <section className="mt-8">
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-line text-3xl" />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">开始搜索你喜欢的影视</h2>
              <p className="mt-2 text-sm text-foreground-500">试试输入片名、类型或演员名字。</p>
            </div>

            <div className="mt-10">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-fire-line text-accent-600" /> 热门推荐
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {getHotMovies(8).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">热门类型：</span>
              {(Object.keys(TYPE_LABELS) as MovieType[]).map((t) => (
                <Link
                  key={t}
                  to={`/category/${t}`}
                  className="rounded-md bg-secondary-100 px-3 py-1.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {TYPE_LABELS[t]}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}